<template>
  <div class="home relative">
    <div v-if="isLoading" class="skeleton w-full h-screen"></div>
    <div v-else>
      <div class="relative">
        <img
          v-if="pageContent.imageUrl"
          class="home-image w-full h-screen object-cover object-top"
          :src="pageContent.imageUrl"
          alt="Sqwerv band photo"
        />
        <div
          v-if="isUploading"
          class="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center"
        >
          <div class="loader"></div>
        </div>
        <div v-if="user" class="absolute bottom-4 left-4 z-20">
          <label class="btn btn-primary">
            Change Image
            <input
              type="file"
              accept="image/*"
              class="hidden"
              @change="handleImageUpload"
            />
          </label>
        </div>
        <social-navbar class="z-10" :is-home="true" />
      </div>
      <news />
      <listen />
      <shows class="container mx-auto max-w-screen-lg" />
    </div>
  </div>
</template>

<script setup>
import { useHead } from '@vueuse/head'
import { ref, onMounted } from 'vue'
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore'
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from 'firebase/storage'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import imageCompression from 'browser-image-compression'
import { useToast } from '@/composables/useToast'
import Listen from '@/views/Listen.vue'
import News from '@/views/News.vue'
import SocialNavbar from '@/components/SocialNavbar.vue'
import Shows from '@/views/Shows.vue'

const pageContent = ref({ imageUrl: '' })
const user = ref(null)
const isUploading = ref(false)
const isLoading = ref(true)
const { showToast } = useToast()

onMounted(async () => {
  try {
    const db = getFirestore()
    const pageRef = doc(db, 'content', 'homePage')
    const pageSnap = await getDoc(pageRef)

    if (pageSnap.exists()) {
      pageContent.value = pageSnap.data()
    }
  } catch (error) {
    console.error('Error fetching home page content:', error)
  } finally {
    isLoading.value = false
  }

  const auth = getAuth()
  onAuthStateChanged(auth, (authedUser) => {
    user.value = authedUser
  })
})

const handleImageUpload = async (event) => {
  const file = event.target.files[0]
  if (!file) return

  isUploading.value = true

  const options = {
    maxSizeMB: 1,
    maxWidthOrHeight: 1920,
    useWebWorker: true,
  }

  try {
    const compressedFile = await imageCompression(file, options)
    const storage = getStorage()
    const imageRef = storageRef(storage, `home/home-hero.jpg`)
    await uploadBytes(imageRef, compressedFile)
    const newImageUrl = await getDownloadURL(imageRef)

    const db = getFirestore()
    const pageRef = doc(db, 'content', 'homePage')
    await setDoc(pageRef, { imageUrl: newImageUrl }, { merge: true })
    pageContent.value.imageUrl = newImageUrl

    showToast('Image updated successfully!', 'success')
  } catch (error) {
    console.error('Error uploading image:', error)
    showToast('Failed to update image.', 'error')
  } finally {
    isUploading.value = false
  }
}

useHead({
  title: 'Home Page - Sqwerv',
  meta: [
    {
      hid: 'sqwervHomePage',
      name: 'description',
      content:
        'Sqwerv is a 4 piece Indie-Psych-Rock band that took their passion for classic rock and psychedelic music, fused it with a dose of funk, and created a sound that is refreshingly original. When Sqwerv takes the stage their songwriting shines as audiences are taken on a ride through surreal stories, ethereal swirling guitar solos and bellowing bumblebee baselines.',
    },
  ],
})
</script>

<style scoped>
@media screen and (max-width: 1100px) {
  .home-image {
    object-position: 40% 38%;
  }
}
@media screen and (max-width: 620px) {
  .home-image {
    object-position: 50% 38%;
  }
}

.loader {
  border: 8px solid #f3f3f3;
  border-top: 8px solid #3498db;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  animation: spin 2s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
